import { useEffect } from 'react';
import { z } from 'zod';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { FEATURE_FLAGS } from 'shared/consts';
import { useFeatureFlagWithStatus } from 'shared/hooks';
import { prefixKey } from 'shared/utils/localStorage';

import { DARK_CLASS_NAME, THEMES, THEME_STORAGE_KEY, type Theme } from '../consts';
import { resolveTheme } from '../utils';

const persistedThemeSchema = z.object({ theme: z.enum([THEMES.LIGHT, THEMES.DARK]) });

interface ThemeStore {
  /** The persisted colour-scheme preference; light until the user opts in. */
  theme: Theme;
  /** Persists a new preference for every subscribed caller at once. */
  setTheme: (theme: Theme) => void;
}

/**
 * Single owner of the colour-scheme preference. Every `useTheme` caller
 * (the floating `DarkModeSwitcher` today) subscribes to this store, so any
 * future control stays in sync automatically — per-instance state would let
 * them drift apart. Persisted under `brightlocal_theme`; the pre-paint script
 * in `index.html` reads the same key, so keep the two in sync.
 */
export const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      theme: THEMES.LIGHT,
      setTheme: (theme) => set({ theme })
    }),
    {
      name: prefixKey(THEME_STORAGE_KEY),
      // An unknown or corrupt stored value falls back to light instead of
      // poisoning the store — mirrors resolveTheme's fail-to-light stance.
      merge: (persisted, current) => {
        const parsed = persistedThemeSchema.safeParse(persisted);

        return { ...current, theme: parsed.success ? parsed.data.theme : THEMES.LIGHT };
      }
    }
  )
);

/** Return shape of {@link useTheme}. */
interface UseThemeReturn {
  /**
   * Whether dark mode resolves as active. The document classes follow once
   * flag availability settles — until then the pre-paint script's classes
   * are left untouched.
   */
  isDarkMode: boolean;
  /**
   * Whether the dark-mode switcher may be shown — the `dark-mode-allowed`
   * flag, resolved the same way on every environment. Gates the floating
   * `DarkModeSwitcher`.
   */
  isDarkModeAllowed: boolean;
  /** Persists the preference; the document class follows on the next render. */
  setIsDarkMode: (isDark: boolean) => void;
}

/**
 * Owns the colour-scheme preference: persists it via {@link useThemeStore}
 * and applies the design system's dark class to the document.
 *
 * The class goes on **both** `<html>` and `<body>` because the DS packages are
 * split on where they expect it — `@brightlocal/tokens` documents `<html>`,
 * while its own `body.dark` rule and `useMapStyles`' MutationObserver read
 * `<body>`. Setting both keeps maps and tokens in agreement.
 *
 * Availability is the `dark-mode-allowed` PostHog flag — the same gate on
 * every environment (each env's build reads its own PostHog project, so the
 * flag must exist per project). Each person's choice lives in their own
 * browser, so engineers and designers switch independently.
 *
 * Degraded path: if neither flag source ever answers (PostHog blocked — e.g.
 * an ad blocker — on a logged-out page), the document keeps whatever the
 * pre-paint script applied: the user's own stored preference, rather than a
 * flash to light on a guess. Auth/me settles the flag after login.
 *
 * Safe to call from multiple components at once — all callers share
 * {@link useThemeStore}, so every control stays in sync.
 */
export const useTheme = (): UseThemeReturn => {
  const { isEnabled: isDarkModeAllowed, isReady: isFlagReady } = useFeatureFlagWithStatus(
    FEATURE_FLAGS.DARK_MODE_ALLOWED
  );
  const { theme, setTheme } = useThemeStore();

  const isDarkMode = resolveTheme(theme, isDarkModeAllowed) === THEMES.DARK;

  useEffect(() => {
    // Keep whatever the pre-paint script applied until the flag settles —
    // toggling on the fail-closed placeholder would strip the pre-painted
    // dark class and flash light before the flag arrives.
    if (!isFlagReady) {
      return;
    }

    document.documentElement.classList.toggle(DARK_CLASS_NAME, isDarkMode);
    document.body.classList.toggle(DARK_CLASS_NAME, isDarkMode);
  }, [isDarkMode, isFlagReady]);

  const setIsDarkMode = (isDark: boolean) => setTheme(isDark ? THEMES.DARK : THEMES.LIGHT);

  return { isDarkMode, isDarkModeAllowed, setIsDarkMode };
};
